import { DesignerParams } from '@/types';
import { checkContrastAgainstWhite } from '@/utils/colors/checkContrastAgainstWhite';
import { initFaqLocale } from '@/utils/faq';
import { getRoute, initHistory, navigate } from '@/utils/history';
import { init as initI18n } from '@/utils/i18n';
import { setParams } from '@/utils/params';
import { enableMementoCheckAction } from '@/redux/core/actions/enableMementoCheckAction';
import { isMementoAvailable } from '@/redux/draft/memento/isMementoAvailable';
import { startWritingToMemento } from '@/redux/draft/memento/status';
import { setupStore } from '@/redux/setupStore';
import { ExternalInterface } from './ExternalInterface';

let preferredHeightCallback: () => void;
let preferredHeight = -1;
let preferredHeightObserver: MutationObserver | ResizeObserver;

// prevent user from accidentally zooming the whole viewport
function preventAccidentalZoom() {
  document.addEventListener(
    'touchmove',
    (event) => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    },
    { passive: false }
  );
}

function addCustomCTAColor(params: DesignerParams, target: HTMLElement) {
  const { ctaColor, ctaColorHover, ctaColorActive, ctaColorInactive } = params;
  if (!ctaColor && !ctaColorHover && !ctaColorActive && !ctaColorInactive) {
    return; // no colors defined
  } else if (!ctaColor || !ctaColorHover || !ctaColorActive || !ctaColorInactive) {
    return console.warn('Not all call-to-action colors have been defined.');
  }

  if (!checkContrastAgainstWhite(ctaColor)) {
    return console.warn(
      'The main call-to-action color has insufficient contrast with the white background.'
    );
  }

  const { style } = target;
  style.setProperty('--cta', ctaColor);
  style.setProperty('--cta-hover', ctaColorHover);
  style.setProperty('--cta-active', ctaColorActive);
  style.setProperty('--cta-inactive', ctaColorInactive);
}

function initPreferredHeightObserver(parameter: DesignerParams) {
  if (preferredHeightObserver) {
    preferredHeightObserver.disconnect();
  }

  if (preferredHeightCallback) {
    window.removeEventListener('resize', preferredHeightCallback);
  }

  const onPreferredHeightChanged = parameter.onPreferredHeightChanged;
  if (onPreferredHeightChanged) {
    const heightCallback = () => {
      const newHeight = document.body.scrollHeight;
      if (newHeight !== preferredHeight) {
        preferredHeight = newHeight;
        onPreferredHeightChanged(newHeight);
      }
    };

    let observer;
    if (window.ResizeObserver) {
      observer = new ResizeObserver(heightCallback);
      observer.observe(document.body);
    } else {
      heightCallback();
      window.addEventListener('resize', heightCallback);
      observer = new MutationObserver(heightCallback);
      observer.observe(document.body, { childList: true, attributes: true, subtree: true });
    }

    preferredHeightCallback = heightCallback;
    preferredHeightObserver = observer;
  }
}

export async function bootstrap(
  name: 'sketchomat' | 'smartomat' | 'templomat',
  target: HTMLElement,
  params: DesignerParams,
  callback: (error?: Error, externalInterface?: ExternalInterface) => void = (error) => {
    if (error) {
      console.error(error);
    }
  }
) {
  try {
    preventAccidentalZoom();

    const client =
      name === 'sketchomat' ? 'Sketchomat' : name === 'templomat' ? 'Templomat' : 'Smartomat';
    const applicationName = params.embroideryMode ? 'stitchomat' : name;
    setParams({ ...params, application: applicationName, client });

    initFaqLocale(params.locale);
    initHistory(name);

    const activeRoute = getRoute(params);

    const [[store, bootstrapType], { default: renderApp }] = await Promise.all([
      setupStore(params, applicationName, activeRoute),
      import(/* webpackChunkName: '[request]' */ `./${client}`),
      initI18n(params.locale),
    ]);

    if (isMementoAvailable(params.contextId, params.platform)) {
      store.dispatch(enableMementoCheckAction());
    } else if (bootstrapType !== 'template') {
      startWritingToMemento();
    }

    if (activeRoute || bootstrapType === 'empty') {
      navigate(activeRoute || 'get-started', true);
    }

    if (!target) {
      target = document.createElement('div');
      document.body.appendChild(target);
    }

    const dialogContainer = document.createElement('div');
    dialogContainer.id = 'modal-container';
    target.parentNode!.insertBefore(dialogContainer, target.nextSibling);

    addCustomCTAColor(params, target.parentElement!);

    renderApp(store, target);

    initPreferredHeightObserver(params);
    callback && callback(undefined, new ExternalInterface(store));
  } catch (error) {
    console.error('Failed to bootstrap designer!', error);
    callback && callback(error as Error);
  }
}
